import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { fetchProjects, setLoadTestJob } from '../services/api';

// The project list is long (every job on file), so the search shows the best
// 50 matches on name, number or city.
const filterProjects = createFilterOptions({
  limit: 50,
  stringify: (p) => [p.projectName, p.projectNumber, p.city, p.state].filter(Boolean).join(' '),
});

const projectLabel = (p) => (p ? [p.projectName, p.projectNumber, [p.city, p.state].filter(Boolean).join(', ')].filter(Boolean).join(' | ') : '');

// Fetched once per page load and shared by every dialog.
let projectsPromise = null;
function loadProjects() {
  if (!projectsPromise) {
    projectsPromise = fetchProjects()
      .then((data) => (Array.isArray(data) ? data : data?.projects || []))
      .catch((err) => {
        projectsPromise = null;
        throw err;
      });
  }
  return projectsPromise;
}

function describeTest(test) {
  const start = test?.start_time ? new Date(test.start_time) : null;
  const end = test?.end_time ? new Date(test.end_time) : null;
  const time = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  const when = start && !Number.isNaN(start.getTime())
    ? `${start.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })} ${time(start)}${end && !Number.isNaN(end.getTime()) ? `–${time(end)}` : ''}`
    : null;
  return [when, test?.box_name, test?.logger_name, test?.peak_kips != null ? `${Math.round(test.peak_kips)} kips peak` : null]
    .filter(Boolean).join(' · ');
}

// Set a recorded test's job (a project on file), point and test number by
// hand. `test` is a row in the History list's shape; onSaved gets the row back
// with the project name, location and who set it.
function AssignJobDialog({ open, test, onClose, onSaved }) {
  const [projects, setProjects] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [project, setProject] = useState(null);
  const [point, setPoint] = useState('');
  const [testNumber, setTestNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    loadProjects().then(setProjects).catch((err) => setLoadError(err.message));
  }, [open]);

  // Start from what the test has now each time the dialog opens.
  useEffect(() => {
    if (!open || !test) return;
    setPoint(test.point_number || '');
    setTestNumber(test.test_number ?? '');
    setError(null);
  }, [open, test]);

  const current = useMemo(() => {
    if (!test?.project_number || !projects) return null;
    return projects.find((p) => p.projectNumber === test.project_number) || null;
  }, [projects, test]);
  useEffect(() => { if (open) setProject(current); }, [open, current]);

  const testNumberValid = String(testNumber).trim() === '' || /^\d+$/.test(String(testNumber).trim());

  const save = async () => {
    if (!testNumberValid) {
      setError('Test number must be a whole number.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { test: row } = await setLoadTestJob(test.id, {
        project_number: project?.projectNumber || '',
        point_number: point.trim(),
        test_number: String(testNumber).trim(),
      });
      onSaved(row);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const loggerSentNoJob = test && !test.job_set_by && !test.job_number;
  const setBy = test?.job_set_by
    ? `Last set by ${test.job_set_by}${test.job_set_at ? `, ${new Date(test.job_set_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}.`
    : null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ pb: 0.5 }}>{test?.job_number || test?.job_set_by ? 'Edit job' : 'Assign job'}</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>{describeTest(test)}</Typography>
        <Stack spacing={2}>
          <Autocomplete
            options={projects || []}
            loading={!projects && !loadError}
            value={project}
            onChange={(_, value) => setProject(value)}
            filterOptions={filterProjects}
            getOptionLabel={projectLabel}
            isOptionEqualToValue={(a, b) => a.projectNumber === b.projectNumber}
            renderOption={(props, p) => (
              <Box component='li' {...props} key={p.projectNumber}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' component='div'>{p.projectName}</Typography>
                  <Typography variant='caption' color='text.secondary' component='div'>
                    {[p.projectNumber, [p.city, p.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Project'
                placeholder='Search by name, number or city'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {!projects && !loadError ? <CircularProgress color='inherit' size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {test?.job_number && !current && projects && (
            <Typography variant='caption' color='text.secondary'>
              The logger sent job {test.job_number}, which doesn&apos;t match one project on file. Pick the project to set it.
            </Typography>
          )}
          <Stack direction='row' spacing={2}>
            <TextField label='Point' value={point} onChange={(e) => setPoint(e.target.value)} inputProps={{ maxLength: 20 }} fullWidth />
            <TextField
              label='Test number'
              value={testNumber}
              onChange={(e) => { setTestNumber(e.target.value); setError(null); }}
              error={!testNumberValid}
              helperText={!testNumberValid ? 'A whole number' : ' '}
              inputProps={{ inputMode: 'numeric' }}
              fullWidth
            />
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            {setBy || (loggerSentNoJob ? 'The logger sent no job for this test.' : 'From the logger.')}
            {' '}Once set here, the logger&apos;s values don&apos;t replace it.
          </Typography>
          {loadError && <Alert severity='error'>Couldn&apos;t load the project list: {loadError}</Alert>}
          {error && <Alert severity='error'>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving || !test} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignJobDialog;
